import type { IPositionMeasurement } from '@lucimark/shared/models/geolocation'
import type { TGeolocationClientErrorPayload } from '@lucimark/shared/models/geolocation-client-error'
import type { IPlayerUpdateEventPayload, IWebOtaUpdateEventPayload } from '@lucimark/shared/models/player-update'
import type { TWebOtaApplyTiming } from '@lucimark/shared/models/tenant'
import type {
  TAmbiguousDirectoryGateResult,
  TRegistrationClearDirectoryGateMode
} from '@/helpers/tryAbortAmbiguousClearViaDirectory'
import type { TAuthenticationService } from '@/services/AuthenticationService'
import type WebSocketMessageProcessor from '@/services/WebSocketMessageProcessor'
import Bottleneck from 'bottleneck'
import Emittery from 'emittery'
import { getPlatformHeader, MODE } from '@/constants'
import { getApiURL } from '@/helpers/getCurrentURLS'
import {
  recordPlayerAuthAmbiguousFailure,
  registrationBackupDispositionForPersistedClear,
  resetPlayerAuthAmbiguousFailureStreak,
  shouldClearPersistedRegistration,
  tryAcquireAmbiguousClearCooldown
} from '@/helpers/playerRegistrationAuthPolicy'
import { reportRegistrationClearedToSentry } from '@/helpers/reportRegistrationClearedToSentry'
import { computeWebSocketReconnectBackoffMs } from '@/helpers/webSocketReconnectBackoff'
import WebSocketClient, { WebSocketClientEvent, WebSocketClientState } from '@/services/WebSocketClient'

export enum WebSocketServiceEvent {
  STATE_UPDATE = 'state-update',
  DISCONNECTED = 'disconnected',
  RTT = 'rtt',
  UPDATE_AVAILABLE = 'update-available',
  WEB_OTA_AVAILABLE = 'web-ota-available'
}

export interface IWebSocketDisconnectedPayload {
  code: number
  reason: string
  wasClean: boolean
}

/** Web OTA offer from WS handshake (or null = explicit no offer / rollback signal). */
export interface IWebOtaAvailableEventPayload {
  webOta: IWebOtaUpdateEventPayload | null
  webOtaApplyTiming?: TWebOtaApplyTiming
}

/** Native APK offer from WS handshake; timing mirrors tenant `playerWebOtaApplyTiming` when registered. */
export interface IApkUpdateAvailableEventPayload {
  update: IPlayerUpdateEventPayload
  webOtaApplyTiming?: TWebOtaApplyTiming
}

export interface WebSocketServiceEventMap {
  [WebSocketServiceEvent.STATE_UPDATE]: WebSocketClientState
  [WebSocketServiceEvent.DISCONNECTED]: IWebSocketDisconnectedPayload
  [WebSocketServiceEvent.RTT]: number
  [WebSocketServiceEvent.UPDATE_AVAILABLE]: IApkUpdateAvailableEventPayload
  [WebSocketServiceEvent.WEB_OTA_AVAILABLE]: IWebOtaAvailableEventPayload
}

export interface IWebSocketService {
  webSocketClientState: WebSocketClientState
  sendPosition: (position: IPositionMeasurement) => Promise<void>
  sendGeolocationClientError: (payload: TGeolocationClientErrorPayload) => Promise<void>
}

export default class WebSocketService extends Emittery<WebSocketServiceEventMap> implements IWebSocketService {
  readonly authenticationService: TAuthenticationService
  readonly webSocketClient: WebSocketClient
  private _webSocketClientState: WebSocketClientState = WebSocketClientState.DISCONNECTED
  private webSocketMessageProcessor: WebSocketMessageProcessor
  private reconnectLimiter: Bottleneck
  private _rtt: number = -1
  private readonly _directoryGate: ((mode: TRegistrationClearDirectoryGateMode) => Promise<TAmbiguousDirectoryGateResult>) | undefined

  constructor(params: {
    authenticationService: TAuthenticationService
    webSocketMessageProcessor: WebSocketMessageProcessor
    getAppVersion: () => string | Promise<string>
    /** Web bundle version (e.g. OTA hash or web commit). Optional; sent as x-player-web-version when present. */
    getWebVersion?: () => string | null | Promise<string | null>
    /** Before clearing persisted registration, confirm with Players Directory (and tenant status for ambiguous). */
    directoryGate?: (mode: TRegistrationClearDirectoryGateMode) => Promise<TAmbiguousDirectoryGateResult>
  }) {
    super()
    const { authenticationService, webSocketMessageProcessor, getAppVersion, getWebVersion, directoryGate } = params
    this.authenticationService = authenticationService
    this.webSocketMessageProcessor = webSocketMessageProcessor
    this._directoryGate = directoryGate

    this.webSocketClient = new WebSocketClient({
      platform: () => getPlatformHeader(),
      getAppVersion,
      getWebVersion,
      mode: MODE,
      baseURL: getApiURL,
      authenticationService
    })

    this.webSocketClient.on(WebSocketClientEvent.STATE_UPDATE, (state) => {
      this._webSocketClientState = state
      void this.emit(WebSocketServiceEvent.STATE_UPDATE, state)
    })

    this.webSocketClient.on(WebSocketClientEvent.MESSAGE, async (rawMsg) => {
      await this.webSocketMessageProcessor.parseMsg(rawMsg)
    })

    this.webSocketClient.on(WebSocketClientEvent.DISCONNECTED, (payload) => {
      void this.emit(WebSocketServiceEvent.DISCONNECTED, payload)
      void this.start()
    })

    this.webSocketClient.on(WebSocketClientEvent.AUTHENTICATION_ERROR, async (payload) => {
      const status = payload.status ?? 401
      const code = payload.code
      const decision = shouldClearPersistedRegistration({ code, status })
      if (decision === 'clear') {
        const revokeGate = this._directoryGate != null
          ? await this._directoryGate('revoke')
          : 'proceed_clear' as TAmbiguousDirectoryGateResult
        if (revokeGate !== 'proceed_clear') {
          console.warn('[player_auth] skipped server-revoke WS registration clear (directory gate)', { revokeGate, code, status })
        }
        else {
          console.warn('[player_auth] clearing registration after WS preflight', { code, status })
          reportRegistrationClearedToSentry({
            reason: 'server_revoke',
            source: 'websocket',
            context: 'ws_preflight',
            code,
            httpStatus: status
          })
          await this.authenticationService.setRegistration(null, {
            registrationBackup: registrationBackupDispositionForPersistedClear('server_revoke')
          })
        }
      }
      else if (decision === 'ambiguous' && recordPlayerAuthAmbiguousFailure('websocket')) {
        const gate = this._directoryGate != null
          ? await this._directoryGate('ambiguous')
          : 'proceed_clear' as TAmbiguousDirectoryGateResult
        if (gate !== 'proceed_clear') {
          console.warn('[player_auth] skipped ambiguous WS registration clear (directory gate)', { gate, code, status })
        }
        else if (!tryAcquireAmbiguousClearCooldown()) {
          console.warn('[player_auth] skipped ambiguous WS registration clear (cooldown active)', { code, status })
        }
        else {
          console.warn('[player_auth] clearing registration after ambiguous WS auth streak', { code, status })
          reportRegistrationClearedToSentry({
            reason: 'ambiguous_streak',
            source: 'websocket',
            context: 'ws_preflight',
            code,
            httpStatus: status
          })
          await this.authenticationService.setRegistration(null, {
            registrationBackup: registrationBackupDispositionForPersistedClear('ambiguous_streak')
          })
          resetPlayerAuthAmbiguousFailureStreak()
        }
      }
      void this.start()
    })

    this.webSocketClient.on(WebSocketClientEvent.RTT, (rtt) => {
      this.rtt = rtt
    })

    this.reconnectLimiter = new Bottleneck({
      maxConcurrent: 1,
      minTime: 1000,
      highWater: 1,
      strategy: Bottleneck.strategy.OVERFLOW
    })

    this.reconnectLimiter.on('failed', async (error, jobInfo) => {
      // Exponential backoff, clamped to 30s (never reset after the cap — fleet reconnect storms).
      const expBackoffDelayMs = computeWebSocketReconnectBackoffMs(jobInfo.retryCount)

      console.warn(
        `${jobInfo.options.id} #${
          jobInfo.retryCount
        } => connect failed: ${(error as Error).message}, retrying in ${(expBackoffDelayMs / 1000).toFixed(
          1
        )} seconds...`
      )
      return expBackoffDelayMs
    })
  }

  get rtt() {
    return this._rtt
  }

  set rtt(rtt: number) {
    this._rtt = rtt
    void this.emit(WebSocketServiceEvent.RTT, this._rtt)
  }

  get webSocketClientState() {
    return this._webSocketClientState
  }

  async start() {
    try {
      await this.reconnectLimiter.schedule({}, async () => {
        const { update, webOta, webOtaApplyTiming } = await this.webSocketClient.authenticate()
        // Emit update from handshake regardless of tenant registration so unregistered players can update.
        if (update !== null) {
          await this.emit(WebSocketServiceEvent.UPDATE_AVAILABLE, {
            update,
            ...(webOtaApplyTiming !== undefined ? { webOtaApplyTiming } : {})
          })
        }
        // Emit web-OTA offer (or "no offer") from handshake.
        // The player uses `null` to trigger rollback/revert.
        await this.emit(WebSocketServiceEvent.WEB_OTA_AVAILABLE, {
          webOta,
          ...(webOtaApplyTiming !== undefined ? { webOtaApplyTiming } : {})
        })
        await this.webSocketClient.connect()
      })
    }
    catch (err) {
      // OVERFLOW strategy drops excess reconnect jobs — expected, not an error
      if (err instanceof Error && err.message.includes('dropped by Bottleneck')) {
        return
      }
      throw err
    }
  }

  async stop() {
    await this.webSocketClient.disconnect()
  }

  async sendPosition(position: IPositionMeasurement) {
    await this.webSocketClient.sendMessage(JSON.stringify({ type: 'position', payload: position }))
  }

  async sendGeolocationClientError(payload: TGeolocationClientErrorPayload) {
    await this.webSocketClient.sendMessage(JSON.stringify({ type: 'geolocation-error', payload }))
  }
}

export type TWebSocketService = WebSocketService
